import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('change', this.#handleSubscriptionSelectorChange, { signal });
    document.addEventListener('click', this.#handleOneTimeModeClick, { signal });
    this.addEventListener('click', this.#handleStickyPlanToggleClick, { signal });
    this.addEventListener('change', this.#handleStickyPlanChange, { signal });

    this.#getInitialQuantity();
    this.#applyInitialSellingPlan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const wasPlanSelectorOpen = this.#isPlanSelectorOpen();

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    const defaultSellingPlanId = newStickyAddToCart.dataset.defaultSellingPlanId || '';
    const defaultSellingPlanVariantId = newStickyAddToCart.dataset.defaultSellingPlanVariantId || '';

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.dataset.defaultSellingPlanId = defaultSellingPlanId;
    this.dataset.defaultSellingPlanVariantId = defaultSellingPlanVariantId;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#applyInitialSellingPlan();

    if (wasPlanSelectorOpen) {
      this.#openStickyPlanSelect();
    }
  };

  /**
   * Handles clicks on the sticky plan toggle button.
   * @param {Event} event
   */
  #handleStickyPlanToggleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggleButton = target.closest('[data-sticky-plan-toggle]');
    if (!(toggleButton instanceof HTMLButtonElement)) return;

    this.#openStickyPlanSelect();
  };

  /**
   * Handles sticky selling plan select changes.
   * @param {Event} event
   */
  #handleStickyPlanChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-sticky-selling-plan-select]')) return;
    const selectedOption = target.selectedOptions[0];
    const variantId = selectedOption?.dataset.variantId || '';
    this.#syncSellingPlanInput(target.value, variantId);
    this.#updateStickyButtonPricing(selectedOption);
  };

  /**
   * Mirrors main subscription selector radio changes into sticky selector.
   * @param {Event} event
   */
  #handleSubscriptionSelectorChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.classList.contains('product-subscription-selector__radio')) return;
    if (!target.checked) return;

    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement) || !section.contains(target)) return;

    const accordion = target.closest('accordion-custom');
    if (!(accordion instanceof HTMLElement)) return;

    const sellingPlanId = accordion.dataset.sellingPlanId || '';
    const variantId = accordion.dataset.variantId || '';
    this.#setStickyPlanSelection(sellingPlanId, variantId);
  };

  /**
   * Mirrors one-time mode clicks into sticky selector.
   * @param {Event} event
   */
  #handleOneTimeModeClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-one-time-purchase-trigger]') : null;
    if (!(target instanceof HTMLButtonElement)) return;

    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement) || !section.contains(target)) return;

    this.#setStickyPlanSelection('');
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Applies the initial sticky selling plan state to the product form.
   */
  #applyInitialSellingPlan() {
    const stickyPlanSelect = this.querySelector('[data-sticky-selling-plan-select]');
    if (!(stickyPlanSelect instanceof HTMLSelectElement)) {
      this.#syncSellingPlanInput('');
      return;
    }

    const defaultPlanId = this.dataset.defaultSellingPlanId || '';
    const defaultPlanVariantId = this.dataset.defaultSellingPlanVariantId || '';
    if (defaultPlanId !== '') {
      stickyPlanSelect.value = defaultPlanId;
      const selectedOption = stickyPlanSelect.selectedOptions[0];
      const optionVariantId = selectedOption?.dataset.variantId || defaultPlanVariantId;
      this.#syncSellingPlanInput(stickyPlanSelect.value, optionVariantId);
      this.#updateStickyButtonPricing(selectedOption);
      return;
    }

    const selectedOption = stickyPlanSelect.selectedOptions[0];
    const optionVariantId = selectedOption?.dataset.variantId || '';
    this.#syncSellingPlanInput(stickyPlanSelect.value, optionVariantId);
    this.#updateStickyButtonPricing(selectedOption);
  }

  /**
   * Returns true when the sticky plan dropdown is currently shown.
   * @returns {boolean}
   */
  #isPlanSelectorOpen() {
    const selectWrap = this.querySelector('[data-sticky-plan-select-wrap]');
    if (!(selectWrap instanceof HTMLElement)) return false;
    return !selectWrap.hasAttribute('hidden');
  }

  /**
   * Hides the trigger and shows the sticky plan select in place.
   */
  #openStickyPlanSelect() {
    const selectWrap = this.querySelector('[data-sticky-plan-select-wrap]');
    const toggleButton = this.querySelector('[data-sticky-plan-toggle]');
    const select = this.querySelector('[data-sticky-selling-plan-select]');

    if (!(selectWrap instanceof HTMLElement) || !(toggleButton instanceof HTMLButtonElement)) return;

    selectWrap.removeAttribute('hidden');
    toggleButton.hidden = true;
    toggleButton.setAttribute('aria-expanded', 'true');

    if (select instanceof HTMLSelectElement) {
      select.focus();
    }
  }

  /**
   * Sets sticky dropdown to the provided plan/variant and applies sync.
   * @param {string} sellingPlanId
   * @param {string} [variantId]
   */
  #setStickyPlanSelection(sellingPlanId, variantId = '') {
    const select = this.querySelector('[data-sticky-selling-plan-select]');
    if (!(select instanceof HTMLSelectElement)) return;

    const options = Array.from(select.options);
    let matchedOption = null;

    if (sellingPlanId) {
      matchedOption = options.find((option) => {
        if (option.value !== String(sellingPlanId)) return false;
        if (!variantId) return true;
        return (option.dataset.variantId || '') === String(variantId);
      });
    } else {
      matchedOption = options.find((option) => option.value === '');
    }

    if (!matchedOption) return;

    if (select.value !== matchedOption.value || select.selectedOptions[0] !== matchedOption) {
      matchedOption.selected = true;
    }

    const selectedVariantId = matchedOption.dataset.variantId || '';
    this.#syncSellingPlanInput(matchedOption.value, selectedVariantId);
    this.#updateStickyButtonPricing(matchedOption);
  }

  /**
   * Syncs selected selling plan id into the actual product form hidden input.
   * @param {string} sellingPlanId
   * @param {string} [variantId]
   */
  #syncSellingPlanInput(sellingPlanId, variantId = '') {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const sellingPlanInput = productForm.querySelector('input[data-subscription-selling-plan][name="selling_plan"]');
    if (!(sellingPlanInput instanceof HTMLInputElement)) return;

    sellingPlanInput.value = sellingPlanId || '';
    this.#syncSubscriptionAccordionSelection(sellingPlanId, variantId);

    if (variantId) {
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.value = variantId;
      }
      this.dataset.currentVariantId = variantId;
    }
  }

  /**
   * Mirrors sticky selection into the main subscription accordion state used on submit.
   * @param {string} sellingPlanId
   * @param {string} variantId
   */
  #syncSubscriptionAccordionSelection(sellingPlanId, variantId) {
    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    const root = section.querySelector('[data-subscription-accordion-group]');
    if (!(root instanceof HTMLElement)) return;

    if (!sellingPlanId) {
      root.dataset.stickyPurchaseMode = 'one-time';
      root.dataset.purchaseMode = 'one-time';
      return;
    }

    root.dataset.stickyPurchaseMode = 'subscription';
    root.dataset.purchaseMode = 'subscription';

    const escapedPlanId = window.CSS?.escape ? window.CSS.escape(String(sellingPlanId)) : String(sellingPlanId);
    const escapedVariantId = window.CSS?.escape ? window.CSS.escape(String(variantId)) : String(variantId);
    const accordion = root.querySelector(
      `accordion-custom[data-selling-plan-id="${escapedPlanId}"][data-variant-id="${escapedVariantId}"]`
    );
    if (!(accordion instanceof HTMLElement)) return;

    const radio = accordion.querySelector('.product-subscription-selector__radio');
    if (!(radio instanceof HTMLInputElement)) return;

    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Updates sticky add-to-cart button current/original prices from selected dropdown option.
   * @param {HTMLOptionElement | undefined} selectedOption
   */
  #updateStickyButtonPricing(selectedOption) {
    if (!(selectedOption instanceof HTMLOptionElement)) return;

    const currentPriceEl = this.querySelector('[data-sticky-button-price-current]');
    const originalPriceEl = this.querySelector('[data-sticky-button-price-original]');

    if (!(currentPriceEl instanceof HTMLElement)) return;
    if (!(originalPriceEl instanceof HTMLElement)) return;

    const currentDisplay = (selectedOption.dataset.priceDisplay || '').trim();
    const originalDisplay = (selectedOption.dataset.originalDisplay || '').trim();

    if (currentDisplay !== '') {
      currentPriceEl.textContent = currentDisplay;
    }

    if (originalDisplay !== '' && originalDisplay !== currentDisplay) {
      originalPriceEl.hidden = false;
      originalPriceEl.textContent = originalDisplay;
    } else {
      originalPriceEl.hidden = true;
      originalPriceEl.textContent = originalDisplay || currentDisplay;
    }
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
